import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const WELLNESS_OPTIONS = [
  'General wellness',
  'Fitness / recovery',
  'Stress / sleep',
  'Preventive health',
  'Caregiver / family member',
  'Just curious',
  'Other',
];

const WaitlistPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interest, setInterest] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!consent) { setError('Please agree to the terms before joining.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
          wellnessInterest: interest || undefined,
          consent,
          sourcePage: window.location.pathname,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(data.message ?? 'Something went wrong. Please try again.');
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 4,
    }}>
      <Box sx={{ width: '100%', maxWidth: 520 }}>

        {/* Logo */}
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 800, letterSpacing: '4px',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', mb: 4, textAlign: 'center',
        }}>
          SeeVita
        </Typography>

        {done ? (
          <Box sx={{
            ...cardSx,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', gap: 2, py: 5,
          }}>
            <CheckCircleOutline sx={{ fontSize: 52, color: '#4ade80' }} />
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: 'white' }}>
              You're on the list.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', lineHeight: 1.7 }}>
              We'll reach out when early access opens. Thanks for your interest in SeeVita.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              sx={{ mt: 1, color: '#63b3ed', textTransform: 'none', fontWeight: 700 }}
            >
              Sign in to existing account
            </Button>
          </Box>
        ) : (
          <Box sx={cardSx} component="form" onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: { xs: '1.6rem', sm: '2rem' }, fontWeight: 800, color: 'white', lineHeight: 1.15 }}>
              A simpler way to check in with your body.
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.92rem', lineHeight: 1.7 }}>
              SeeVita is building a contactless wellness app that helps you track key health signals through a quick phone-based scan. Join the waitlist for early access.
            </Typography>

            <Box sx={{ mt: 3.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email address"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                sx={inputSx}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
              />
              <TextField
                label="Name (optional)"
                value={name}
                onChange={e => setName(e.target.value)}
                sx={inputSx}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
              />
              <FormControl>
                <Select
                  displayEmpty
                  value={interest}
                  onChange={e => setInterest(e.target.value)}
                  sx={{
                    ...inputSx['& .MuiOutlinedInput-root'],
                    color: interest ? 'white' : 'rgba(255,255,255,0.45)',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#63b3ed' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.45)' },
                  }}
                  renderValue={v => v || <span style={{ color: 'rgba(255,255,255,0.45)' }}>What brings you here? (optional)</span>}
                >
                  {WELLNESS_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#63b3ed' }, pt: 0.5 }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                  I agree to receive updates from SeeVita. My email will only be used for waitlist communications and will not be shared with third parties.
                </Typography>
              }
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: '10px' }}>{error}</Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              sx={{
                mt: 2.5, py: 1.4, borderRadius: '12px',
                bgcolor: 'white', color: '#0a1628',
                fontWeight: 800, textTransform: 'none', fontSize: '1rem',
                boxShadow: '0 8px 24px rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: '#e8f0fe' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {submitting ? <CircularProgress size={22} sx={{ color: '#0a1628' }} /> : 'Join the waitlist'}
            </Button>

            <Typography sx={{ mt: 2, textAlign: 'center', fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)' }}>
              Already have an account?{' '}
              <RouterLink to="/login" style={{ color: '#63b3ed', textDecoration: 'none', fontWeight: 700 }}>
                Sign in
              </RouterLink>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const cardSx = {
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '20px',
  bgcolor: 'rgba(10,28,60,0.6)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
  p: { xs: 2.5, sm: 3.5 },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#63b3ed' },
  },
};

export default WaitlistPage;
